import React, { useState } from 'react';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Button } from '@mui/material';
import { ListTable } from '../../molecules/ListTable';
import { IUser } from '../../../utils/entities/interface';
import {
  capitalizeWords,
  formatDateAndTime,
  formatPhoneNumber,
  getStatusLabel,
} from '../../../utils/helper';
import { ToggleUserStatus } from '../../../services/users';
import { useNavigate } from 'react-router-dom';
import { ModalConfirmation } from '../../molecules/ModalConfirmation';

export const UserList = ({
  users,
  onUpdate,
  isLoading,
  headerCells,
}: {
  users: IUser[];
  onUpdate: () => void;
  headerCells: [string, number][];
  isLoading: boolean;
}) => {
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);

  const handleOpenModal = (user: IUser) => {
    setSelectedUser(user);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedUser(null);
    setOpenModal(false);
  };

  const handleConfirmToggle = async () => {
    if (!selectedUser) return;
    await ToggleUserStatus(selectedUser._id);
    onUpdate();
    handleCloseModal();
  };

  const actions = [
    {
      icon: <RemoveRedEyeOutlinedIcon />,
      onClick: (user: IUser) => navigate(`/customers/${user._id}`),
    },
  ];

  return (
    <>
      <ListTable
        headerCells={headerCells}
        rows={users}
        isLoading={isLoading}
        actions={actions}
        renderCell={(user: IUser, colIndex: number) => {
          switch (colIndex) {
            case 0:
              return capitalizeWords(user.name) || 'N/A';
            case 1:
              return formatPhoneNumber(user.phoneNumber);
            case 2:
              return ['RETAIL', 'HNI', 'SME', 'LARGE_SCALE', 'OEM'].includes(
                user.type,
              )
                ? getStatusLabel(user.type)
                : 'N/A';
            case 3:
              return user.email || 'N/A';
            case 4:
              return formatDateAndTime(user.createdAt);
            case 5:
              return (
                <Button
                  variant="outlined"
                  size="small"
                  color={user.isActive ? 'error' : 'success'}
                  onClick={() => handleOpenModal(user)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    borderRadius: '8px',
                  }}
                >
                  {user.isActive ? 'Disable' : 'Enable'}
                </Button>
              );
            default:
              return null;
          }
        }}
      />

      {/* Confirmation Modal */}
      <ModalConfirmation
        open={openModal}
        handleClose={handleCloseModal}
        handleConfirm={handleConfirmToggle}
        selectedItem={selectedUser}
        entityName="user"
      />
    </>
  );
};
